import * as React from 'react';
import { useSelector } from 'react-redux';
import Form, { FormInstance } from 'antd/lib/form';
import Select from 'antd/lib/select';
import Tag from 'antd/lib/tag';
import { AddFixRequestWorkItemData } from '@/actions/releaseStep';
import { FixRequestFormFieldValue, IssueEntity, IssueSourceType, ReleaseStatus, ReleaseStepType } from '@/types';
import { RootState } from '@/reducers/reducers';
import { selectAppHome, selectFixRequestFormFieldsSettings, selectReleasesForAppId } from '@/selectors/selectors';
import { generateBackwardsCompatibleReleaseStepRunId } from '@/reducers/releaseStep';
import { runwayColors, rwColors, tagStyle } from '@/components/styles/globalStyles';
import { getFixRequestFormFieldId, runeCount } from '@/helpers';
import MarkdownEditor from '@/components/HOCs/MarkdownEditor';
import Space from 'antd/lib/space';
import { DescriptionPopoverIcon } from '@/components/common/DescriptionPopoverIcon';
import WorkItemSelect, {
    toAddFixRequestWorkItemData,
    useFixWorkItemCandidates,
} from '@/components/featureReadiness/WorkItemSelect';

export interface FixRequestFormValues {
    version?: string;
    [fieldName: string]: string | undefined;
}

const releaseTag = (version?: string, liveVersion?: string, nextVersion?: string): string | undefined => {
    if (version && version === nextVersion) {
        return 'NEXT RELEASE';
    }
    if (version && version === liveVersion) {
        return 'LIVE RELEASE';
    }
    return undefined;
};

const VersionLabel: React.FC<{ label: React.ReactNode; tag?: string }> = ({ label, tag }) => {
    const tagColor = tag === 'LIVE RELEASE' ? rwColors.statusGreen : rwColors.statusYellow;
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 16 }}>
            <span>{label}</span>
            {tag && (
                <Tag
                    style={{
                        ...tagStyle,
                        color: tagColor,
                        borderColor: tagColor,
                        marginInlineEnd: 0,
                        backgroundColor: runwayColors.white,
                    }}
                >
                    {tag}
                </Tag>
            )}
        </span>
    );
};

interface UseFixRequestFormArgs {
    appId: string;
    orgId: string;
    form: FormInstance<FixRequestFormValues>;
    issue?: IssueEntity;
    workItemOptional?: boolean;
}

export const useFixRequestForm = ({ appId, orgId, form, issue, workItemOptional }: UseFixRequestFormArgs) => {
    const formFieldsSettings = useSelector(selectFixRequestFormFieldsSettings(appId));
    const releases = useSelector((state: RootState) => selectReleasesForAppId(state, appId));
    const appHome = useSelector((state: RootState) => selectAppHome(state, appId));
    const liveVersion = appHome?.liveRelease?.version;
    const nextVersion = appHome?.nextRelease?.version;
    const openReleases = releases.filter(
        (release) => ![ReleaseStatus.Completed, ReleaseStatus.Skipped, ReleaseStatus.Deleted].includes(release.status),
    );

    const defaultVersion =
        issue?.targetVersion && openReleases.some((release) => release.version === issue.targetVersion) ?
            issue.targetVersion
        :   nextVersion;
    const selectedVersion = Form.useWatch('version', form) ?? defaultVersion;

    const releaseId = releases.find((release) => release.version === selectedVersion)?.id ?? '';
    const stepRunId =
        releaseId ? generateBackwardsCompatibleReleaseStepRunId(releaseId, ReleaseStepType.FeatureReadiness) : '';
    const workItemCandidates = useFixWorkItemCandidates(stepRunId);

    // The issue's source ticket, offered as a selectable work item even if it isn't a release feature item yet.
    const singleTicketSource =
        issue?.sources?.length === 1 && issue.sources[0].type === IssueSourceType.PMTicket ?
            issue.sources[0].pmTicket
        :   undefined;
    const sourceTicket =
        singleTicketSource?.ticketId ?
            {
                id: `source-ticket:${singleTicketSource.ticketId}`,
                identifier: singleTicketSource.ticketId,
                url: singleTicketSource.ticketUrl,
                title: singleTicketSource.ticketTitle,
                owner: singleTicketSource.assignee || singleTicketSource.reporter,
            }
        :   undefined;

    // A single ticket source IS the fix's work item, so it's pre-filled and locked.
    const sourceTicketWorkItemId = React.useMemo(() => {
        if (!sourceTicket) {
            return undefined;
        }
        const candidateMatch = workItemCandidates.find((item) => item.ticket?.identifier === sourceTicket.identifier);
        return candidateMatch ? candidateMatch.id : sourceTicket.id;
    }, [sourceTicket, workItemCandidates]);

    // True once the open animation finishes and the Form.Items have mounted (destroyOnClose).
    const [hasOpened, setHasOpened] = React.useState(false);

    React.useEffect(() => {
        if (!hasOpened || !sourceTicketWorkItemId) {
            return;
        }
        const selected: string[] = form.getFieldValue('workItems') ?? [];
        if (selected.length === 0) {
            form.setFieldValue('workItems', [sourceTicketWorkItemId]);
            return;
        }
        if (sourceTicket && sourceTicketWorkItemId !== sourceTicket.id && selected.includes(sourceTicket.id)) {
            form.setFieldValue(
                'workItems',
                selected.map((id) => (id === sourceTicket.id ? sourceTicketWorkItemId : id)),
            );
        }
    }, [hasOpened, sourceTicketWorkItemId]);

    // initialValue only applies at mount, and defaultVersion can arrive after it.
    React.useEffect(() => {
        if (!hasOpened || !defaultVersion || form.getFieldValue('version')) {
            return;
        }
        form.setFieldValue('version', defaultVersion);
    }, [hasOpened, defaultVersion]);

    // Required unless an automation will supply a ticket, or a source ticket is already locked in.
    const workItemRequired = !workItemOptional && !sourceTicket;

    // Exposed separately (rather than as one `fields` block) so callers can render other inputs — e.g. the work
    // item picker — between the target version and the custom detail fields.
    const versionField = (
        <Form.Item
            name="version"
            label="Target version"
            layout="horizontal"
            labelAlign="left"
            labelCol={{ flex: '130px' }}
            wrapperCol={{ flex: 'auto' }}
            colon={false}
            initialValue={defaultVersion}
            rules={[{ required: true, message: 'Select a release' }]}
        >
            <Select
                placeholder="Select a release"
                showSearch
                optionFilterProp="label"
                options={openReleases.map((release) => ({
                    value: release.version,
                    label: release.displayableVersion || release.version,
                }))}
                optionRender={(option) => (
                    <VersionLabel
                        label={option.label}
                        tag={releaseTag(option.value as string, liveVersion, nextVersion)}
                    />
                )}
                labelRender={(props) => (
                    <VersionLabel
                        label={props.label}
                        tag={releaseTag(props.value as string, liveVersion, nextVersion)}
                    />
                )}
            />
        </Form.Item>
    );

    const workItemField = (
        <Form.Item
            name="workItems"
            label="Associated work item"
            rules={[{ required: workItemRequired, message: 'Associate at least one work item (ticket or code)' }]}
            required={workItemRequired}
        >
            <WorkItemSelect
                orgId={orgId}
                appId={appId}
                releaseId={releaseId}
                stepRunId={stepRunId}
                multiple
                excludeDoneItems
                disabled={!selectedVersion}
                placeholder="Select a ticket and/or code"
                extraTicket={sourceTicket}
                lockedItemId={sourceTicketWorkItemId}
            />
        </Form.Item>
    );

    const customFields = (
        <>
            {formFieldsSettings.map((field) => (
                <Form.Item
                    key={getFixRequestFormFieldId(field)}
                    name={`customField_${getFixRequestFormFieldId(field)}`}
                    label={
                        <Space size={4}>
                            {field.name}
                            {field.description && <DescriptionPopoverIcon description={field.description} />}
                        </Space>
                    }
                    initialValue={field.prePopulatedText || undefined}
                    rules={[
                        { required: field.required, message: `${field.name} is required` },
                        {
                            validator: (_, value) => {
                                if (
                                    field.minCharacters &&
                                    field.minCharacters > 0 &&
                                    value &&
                                    runeCount(value) < field.minCharacters
                                ) {
                                    return Promise.reject(
                                        new Error(
                                            `${field.name} is too short (${runeCount(value)}/${field.minCharacters})`,
                                        ),
                                    );
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <MarkdownEditor preview="edit" textareaProps={{ placeholder: field.placeholder || undefined }} />
                </Form.Item>
            ))}
        </>
    );

    // Surface minCharacters errors for too-short pre-populated values once the fields are mounted. Call from the
    // modal's afterOpenChange so the Form.Items exist (destroyOnClose).
    const seedOnOpen = () => {
        const fieldErrors = formFieldsSettings
            .filter((field) => {
                if (!field.minCharacters || field.minCharacters <= 0) return false;
                const val = field.prePopulatedText;
                return typeof val === 'string' && val.length > 0 && runeCount(val) < field.minCharacters;
            })
            .map((field) => ({
                name: `customField_${getFixRequestFormFieldId(field)}`,
                errors: [
                    `${field.name} is too short (${runeCount(field.prePopulatedText ?? '')}/${field.minCharacters})`,
                ],
            }));
        if (fieldErrors.length > 0) {
            form.setFields(fieldErrors);
        }
    };

    const buildFormFieldsValues = (values: FixRequestFormValues): FixRequestFormFieldValue[] =>
        formFieldsSettings.map((field) => ({
            fieldId: getFixRequestFormFieldId(field),
            value: values[`customField_${getFixRequestFormFieldId(field)}`] ?? '',
        }));

    // Resolve the selected work item ids into the request payload. Candidates map to their PR/ticket; the off-release
    // source ticket sends its key/url so the backend pulls it into the release.
    const buildWorkItems = (): AddFixRequestWorkItemData[] => {
        const selectedIds: string[] = form.getFieldValue('workItems') ?? [];
        return selectedIds
            .map((id) => {
                const candidate = workItemCandidates.find((item) => item.id === id);
                if (candidate) {
                    return toAddFixRequestWorkItemData(candidate);
                }
                if (sourceTicket && id === sourceTicket.id) {
                    return { id, ticketKey: sourceTicket.identifier, ticketUrl: sourceTicket.url };
                }
                return undefined;
            })
            .filter((item): item is AddFixRequestWorkItemData => !!item);
    };

    const fields = (
        <>
            {versionField}
            {workItemField}
            {customFields}
        </>
    );

    return {
        defaultVersion,
        selectedVersion,
        sourceTicket,
        fields,
        seedOnOpen,
        buildFormFieldsValues,
        buildWorkItems,
        onOpenChange: setHasOpened,
    };
};
